<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import WhatsAppTemplatePreview from 'dashboard/components-next/whatsapp/WhatsAppTemplatePreview.vue';
import {
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: { type: Object, default: null },
  processedParams: { type: Object, default: () => ({}) },
  sampleContact: { type: Object, default: null },
  businessName: { type: String, default: '' },
  categoryLabel: { type: String, default: '' },
});

const { t } = useI18n();

const pointOfView = ref('customer');
const introNoticeType = ref('cloud_api');
const osStyle = ref('ios');
const showIntroNotice = ref(true);
const showReadReceipts = ref(true);
const showVerifiedBadge = ref(true);

const previewModes = computed(() => [
  {
    value: 'customer',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.POV.CUSTOMER'),
  },
  {
    value: 'business',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.POV.BUSINESS'),
  },
]);

const deviceModes = computed(() => [
  {
    value: 'ios',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.DEVICE.IOS'),
    icon: 'i-lucide-smartphone',
  },
  {
    value: 'android',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.DEVICE.ANDROID'),
    icon: 'i-lucide-bot',
  },
]);

const introOptions = computed(() => [
  {
    value: 'cloud_api',
    label: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.INTRO.OPTIONS.CLOUD_API'
    ),
    notice: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.INTRO.NOTICES.CLOUD_API'
    ),
  },
  {
    value: 'partner',
    label: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.INTRO.OPTIONS.PARTNER'
    ),
    notice: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.INTRO.NOTICES.PARTNER'
    ),
  },
  {
    value: 'business_app',
    label: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.INTRO.OPTIONS.BUSINESS_APP'
    ),
    notice: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.INTRO.NOTICES.BUSINESS_APP'
    ),
  },
]);

const selectedIntroOption = computed(
  () =>
    introOptions.value.find(option => option.value === introNoticeType.value) ||
    introOptions.value[0]
);

const selectedIntroNotice = computed(() => selectedIntroOption.value.notice);

const headerComponent = computed(() =>
  props.template
    ? findComponentByType(props.template, COMPONENT_TYPES.HEADER)
    : null
);
const bodyComponent = computed(() =>
  props.template
    ? findComponentByType(props.template, COMPONENT_TYPES.BODY)
    : null
);
const footerComponent = computed(() =>
  props.template
    ? findComponentByType(props.template, COMPONENT_TYPES.FOOTER)
    : null
);
const buttonsComponent = computed(() =>
  props.template
    ? findComponentByType(props.template, COMPONENT_TYPES.BUTTONS)
    : null
);

const countVariables = text => (text?.match(/{{[^}]+}}/g) || []).length;

const variableSummary = count =>
  count === 0
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.BLOCKS.NO_VARIABLES')
    : t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.BLOCKS.VARIABLES', {
        count,
      });

const blockItems = computed(() => {
  if (!props.template) return [];
  const items = [];

  if (headerComponent.value) {
    const format = headerComponent.value.format || 'TEXT';
    items.push({
      key: 'header',
      icon: MEDIA_FORMATS.includes(format)
        ? 'i-lucide-image'
        : 'i-lucide-heading-1',
      title: t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.BLOCKS.HEADER'),
      detail: MEDIA_FORMATS.includes(format)
        ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.BLOCKS.MEDIA', {
            format,
          })
        : variableSummary(countVariables(headerComponent.value.text)),
    });
  }

  if (bodyComponent.value) {
    items.push({
      key: 'body',
      icon: 'i-lucide-align-left',
      title: t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.BLOCKS.BODY'),
      detail: variableSummary(countVariables(bodyComponent.value.text)),
    });
  }

  if (footerComponent.value) {
    items.push({
      key: 'footer',
      icon: 'i-lucide-text-cursor-input',
      title: t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.BLOCKS.FOOTER'),
      detail: t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.BLOCKS.TEXT'),
    });
  }

  const buttons = buttonsComponent.value?.buttons || [];
  if (buttons.length) {
    items.push({
      key: 'buttons',
      icon: 'i-lucide-mouse-pointer-click',
      title: t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.BLOCKS.BUTTONS'),
      detail: t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.BLOCKS.OPTIONS', {
        count: buttons.length,
      }),
    });
  }

  return items;
});

const toggleButtonClass = (currentValue, value) =>
  currentValue === value
    ? 'border-n-teal-7 bg-n-teal-3 text-n-teal-11'
    : 'border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2';

const previewSelectClass =
  'reset-base no-margin !mb-0 h-9 w-full appearance-none rounded-md border border-n-weak bg-n-alpha-1 px-2 text-n-slate-12 outline-none [background-image:none] focus:border-n-brand ltr:pr-8 rtl:pl-8';
</script>

<template>
  <section class="p-4 border rounded-lg border-n-weak bg-n-solid-2">
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0">
        <div class="flex items-center gap-1.5">
          <Icon icon="i-lucide-panels-top-left" class="size-4 text-n-teal-10" />
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.TITLE') }}
          </h3>
        </div>
        <p class="mt-1 text-xs leading-5 text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.SUBTITLE') }}
        </p>
      </div>
      <span
        v-tooltip.top="
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.TOOLTIP')
        "
        class="i-lucide-circle-help size-4 text-n-slate-10 cursor-help"
      />
    </div>

    <div class="grid gap-3 mt-4 text-xs">
      <div>
        <div class="mb-1.5 font-medium text-n-slate-10">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.POV.LABEL') }}
        </div>
        <div class="grid grid-cols-2 gap-2">
          <button
            v-for="mode in previewModes"
            :key="mode.value"
            type="button"
            class="inline-flex h-9 items-center justify-center rounded-md border px-2 font-medium transition-colors"
            :class="toggleButtonClass(pointOfView, mode.value)"
            @click="pointOfView = mode.value"
          >
            {{ mode.label }}
          </button>
        </div>
      </div>

      <div>
        <div class="mb-1.5 font-medium text-n-slate-10">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.DEVICE.LABEL') }}
        </div>
        <div class="grid grid-cols-2 gap-2">
          <button
            v-for="device in deviceModes"
            :key="device.value"
            type="button"
            class="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border px-2 font-medium transition-colors"
            :class="toggleButtonClass(osStyle, device.value)"
            @click="osStyle = device.value"
          >
            <Icon :icon="device.icon" class="size-3.5" />
            {{ device.label }}
          </button>
        </div>
      </div>

      <label class="flex flex-col gap-1">
        <span class="font-medium text-n-slate-10">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.INTRO.LABEL') }}
        </span>
        <div class="relative">
          <select
            v-model="introNoticeType"
            :class="previewSelectClass"
            :title="selectedIntroNotice"
          >
            <option
              v-for="option in introOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <span
            class="i-lucide-chevron-down pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-11 ltr:right-2 rtl:left-2"
          />
        </div>
        <span class="text-[11px] leading-4 text-n-slate-10">
          {{ selectedIntroNotice }}
        </span>
      </label>

      <div class="grid gap-2">
        <label class="flex items-center justify-between gap-3">
          <span class="text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.SHOW_INTRO') }}
          </span>
          <input v-model="showIntroNotice" type="checkbox" />
        </label>
        <label class="flex items-center justify-between gap-3">
          <span class="text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.VERIFIED') }}
          </span>
          <input v-model="showVerifiedBadge" type="checkbox" />
        </label>
        <label class="flex items-center justify-between gap-3">
          <span class="text-n-slate-11">
            {{
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.READ_RECEIPTS')
            }}
          </span>
          <input v-model="showReadReceipts" type="checkbox" />
        </label>
      </div>
    </div>

    <div class="mt-5">
      <WhatsAppTemplatePreview
        v-if="template"
        :template="template"
        :processed-params="processedParams"
        :sample-contact="sampleContact"
        :business-name="businessName"
        :category-label="categoryLabel"
        :point-of-view="pointOfView"
        :intro-notice="selectedIntroNotice"
        :show-intro-notice="showIntroNotice"
        :show-verified-badge="showVerifiedBadge"
        :show-read-receipts="showReadReceipts"
        :os-style="osStyle"
      />
      <div v-else class="flex flex-col gap-3">
        <div class="flex items-center justify-between gap-3 text-xs">
          <div class="flex items-center gap-1.5 text-n-slate-11">
            <Icon icon="i-lucide-eye" class="size-3.5" />
            <span class="font-medium">
              {{ t('WHATSAPP_TEMPLATES.PREVIEW.TITLE') }}
            </span>
          </div>
          <span
            class="px-2 py-1 font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
          >
            {{ categoryLabel }}
          </span>
        </div>
        <div class="flex justify-center">
          <div
            class="w-full max-w-[21rem] rounded-[2rem] bg-[#111B21] p-2 shadow-lg"
          >
            <div
              class="flex min-h-[31rem] flex-col items-center justify-center gap-3 rounded-[1.55rem] bg-[#EFE7DD] px-6 text-center"
            >
              <span
                class="flex items-center justify-center rounded-full size-12 bg-white/90 text-[#075E54]"
              >
                <Icon icon="i-lucide-message-square-text" class="size-5" />
              </span>
              <div>
                <p class="text-sm font-semibold text-[#111B21]">
                  {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.PREVIEW_EMPTY_TITLE') }}
                </p>
                <p class="mt-1 text-xs leading-5 text-[#667781]">
                  {{
                    t('CAMPAIGN.WHATSAPP.CREATE.FORM.PREVIEW_EMPTY_SUBTITLE')
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="pt-4 mt-4 border-t border-n-weak">
      <div class="flex items-center justify-between gap-3">
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.BLOCKS.TITLE') }}
        </h4>
        <span
          v-tooltip.top="
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.BLOCKS.TOOLTIP')
          "
          class="i-lucide-circle-help size-3.5 text-n-slate-10 cursor-help"
        />
      </div>
      <div v-if="blockItems.length" class="grid gap-2 mt-3">
        <div
          v-for="block in blockItems"
          :key="block.key"
          class="flex items-center gap-3 rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2"
        >
          <span
            class="inline-flex items-center justify-center rounded-md size-8 bg-n-alpha-2 text-n-slate-11"
          >
            <Icon :icon="block.icon" class="size-4" />
          </span>
          <div class="min-w-0">
            <div class="text-sm font-medium text-n-slate-12">
              {{ block.title }}
            </div>
            <div class="text-xs truncate text-n-slate-10">
              {{ block.detail }}
            </div>
          </div>
        </div>
      </div>
      <p v-else class="mt-3 text-xs leading-5 text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.THREAD_PREVIEW.BLOCKS.EMPTY') }}
      </p>
    </div>
  </section>
</template>
